import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { CSS3DRenderer, CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import './index.scss'

const TV = ({ iframeSrc = 'https://adz.wtf/mac', className = '' }) => {
  const containerRef = useRef(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const width = container.clientWidth
    const height = container.clientHeight

    const scene = new THREE.Scene()
    const cssScene = new THREE.Scene()

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 1.2))
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8)
    dirLight.position.set(5, 10, 8)
    scene.add(dirLight)
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3)
    fillLight.position.set(-5, 0, -5)
    scene.add(fillLight)

    // Camera — positioned after model loads
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000)
    scene.add(camera)

    // WebGL renderer
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    renderer.setClearColor(0x000000, 0)
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.domElement.style.position = 'absolute'
    renderer.domElement.style.top = '0'
    renderer.domElement.style.left = '0'
    container.appendChild(renderer.domElement)

    // CSS3D renderer — sits behind WebGL canvas
    const cssRenderer = new CSS3DRenderer()
    cssRenderer.setSize(width, height)
    cssRenderer.domElement.style.position = 'absolute'
    cssRenderer.domElement.style.top = '0'
    cssRenderer.domElement.style.left = '0'
    cssRenderer.domElement.style.pointerEvents = 'none'
    container.insertBefore(cssRenderer.domElement, renderer.domElement)

    // Controls — configured after model loads
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.05
    controls.enableZoom = false
    controls.minAzimuthAngle = -Math.PI / 3
    controls.maxAzimuthAngle = Math.PI / 3
    controls.minPolarAngle = Math.PI / 6
    controls.maxPolarAngle = (2.5 * Math.PI) / 6

    const loader = new GLTFLoader()
    loader.load('/models/macintosh_128k_computer_1984.glb', gltf => {
      const model = gltf.scene

      // Force world matrix to be up to date before reading bounds
      model.updateWorldMatrix(true, true)

      // Center and scale model to a consistent world size
      const modelBox = new THREE.Box3().setFromObject(model)
      const modelCenter = modelBox.getCenter(new THREE.Vector3())
      const modelSize = modelBox.getSize(new THREE.Vector3())
      const maxDim = Math.max(modelSize.x, modelSize.y, modelSize.z)
      const targetSize = 10
      const scaleFactor = targetSize / maxDim

      model.scale.setScalar(scaleFactor)
      model.position.sub(modelCenter.multiplyScalar(scaleFactor))
      model.updateWorldMatrix(true, true)

      // Position camera in front of the Mac screen (model faces +Z after scaling)
      const scaledBox = new THREE.Box3().setFromObject(model)
      const scaledCenter = scaledBox.getCenter(new THREE.Vector3())
      const scaledSize = scaledBox.getSize(new THREE.Vector3())

      camera.position.set(
        scaledCenter.x - scaledSize.x * -0.45,
        scaledCenter.y + scaledSize.y * 0.9,
        scaledCenter.z + scaledSize.z * 1.1,
      )
      controls.target.copy(scaledCenter)
      controls.update()

      // Keep only the main computer body — remove keyboard, mouse, keys, brightness knobs
      const toRemove = []
      model.traverse(child => {
        const n = child.name
        if (
          n.includes('Keyboard') ||
          n.includes('Mouse') ||
          n.includes('Keys') ||
          n.includes('Brightness') ||
          n.endsWith('_Keys_0')
        ) {
          toRemove.push(child)
        }
      })
      toRemove.forEach(child => child.parent?.remove(child))

      // Remap body materials to matte black
      const matteBody = new THREE.MeshStandardMaterial({
        color: 0x1c1c1c,
        roughness: 0.85,
        metalness: 0.05,
      })
      const matteRecess = new THREE.MeshStandardMaterial({
        color: 0x111111,
        roughness: 0.9,
        metalness: 0.0,
      })
      const darkMetal = new THREE.MeshStandardMaterial({
        color: 0x1a1a1a,
        roughness: 0.5,
        metalness: 0.4,
      })
      model.traverse(child => {
        if (!child.isMesh) return
        const n = child.name
        if (n === 'Computer_Computer_0' || n === 'Computer_Whiteplastic_0')
          child.material = matteBody
        else if (n === 'Computer_Blackplastic_0') child.material = matteRecess
        else if (n === 'Computer_Metal_0') child.material = darkMetal
      })

      // Find the screen mesh and set up the CSS3D punch-through
      let screenMesh = null
      model.traverse(child => {
        if (child.isMesh && child.name === 'Computer_Screen_0') screenMesh = child
      })

      if (screenMesh) {
        const screenBox = new THREE.Box3().setFromObject(screenMesh)
        const screenCenter = screenBox.getCenter(new THREE.Vector3())
        const screenSize = screenBox.getSize(new THREE.Vector3())

        const worldQuat = new THREE.Quaternion()
        screenMesh.getWorldQuaternion(worldQuat)

        // Replace screen material: zero out alpha to reveal CSS3D behind the canvas
        screenMesh.material = new THREE.MeshBasicMaterial({
          color: 0x000000,
          depthWrite: false,
          blending: THREE.CustomBlending,
          blendEquation: THREE.AddEquation,
          blendSrc: THREE.ZeroFactor,
          blendDst: THREE.ZeroFactor,
          blendEquationAlpha: THREE.AddEquation,
          blendSrcAlpha: THREE.ZeroFactor,
          blendDstAlpha: THREE.ZeroFactor,
        })
        screenMesh.renderOrder = 999

        // Size the iframe to match the screen bounds (512px wide reference)
        const iframeWidth = 512
        const pixelScale = screenSize.x / iframeWidth
        const iframeHeight = Math.round(screenSize.y / pixelScale + 50)

        const iframe = document.createElement('iframe')
        iframe.src = iframeSrc
        iframe.style.width = `${iframeWidth}px`
        iframe.style.height = `${iframeHeight}px`
        iframe.style.border = 'none'
        iframe.style.background = 'none'
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups')

        const cssObject = new CSS3DObject(iframe)
        cssObject.position.copy(screenCenter)
        cssObject.quaternion.copy(worldQuat)
        cssObject.scale.set(pixelScale, pixelScale, pixelScale)
        cssScene.add(cssObject)
      }

      scene.add(model)
    })

    const handleResize = () => {
      const w = container.clientWidth
      const h = container.clientHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
      cssRenderer.setSize(w, h)
    }
    window.addEventListener('resize', handleResize)

    let animFrameId
    const tick = () => {
      animFrameId = requestAnimationFrame(tick)
      controls.update()
      cssRenderer.render(cssScene, camera)
      renderer.render(scene, camera)
    }
    tick()

    return () => {
      cancelAnimationFrame(animFrameId)
      window.removeEventListener('resize', handleResize)
      renderer.dispose()
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement)
      if (cssRenderer.domElement.parentNode === container)
        container.removeChild(cssRenderer.domElement)
    }
  }, [iframeSrc])

  return <div ref={containerRef} className={`tv-canvas ${className}`} />
}

export default TV
